"use client";

import React, { useState, useEffect, useRef } from "react";

const isLeapYear = (year) => {
   return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
};

const getDaysInMonth = (month, year) => {
   if (!month) return 31;
   if ([4, 6, 9, 11].includes(month)) return 30;
   if (month === 2) return year && isLeapYear(year) ? 29 : 28;
   return 31;
};

const formatDateTime = (date, showTime, formatStr) => {
   const YYYY = date.getFullYear().toString();
   const MM = String(date.getMonth() + 1).padStart(2, "0");
   const DD = String(date.getDate()).padStart(2, "0");
   const HH = String(date.getHours()).padStart(2, "0");
   const mm = String(date.getMinutes()).padStart(2, "0");
   
   let result = formatStr
   .replace("YYYY", YYYY)
   .replace("MM", MM)
   .replace("DD", DD);
   
   if (showTime) {
      result = result.replace("HH", HH).replace("mm", mm);
   }
   
   return result;
};

export default function SplitDatePicker({
   value,
   showTime = false,
   dateTimeFormat,
   onChange,
   minYear = 1900,
   maxYear = new Date().getFullYear(),
   onValidationError,
}) {
   const activeFormat = dateTimeFormat || (showTime ? "YYYY-MM-DD HH:mm" : "YYYY-MM-DD");
   
   const parsedInitial = value ? new Date(value) : null;
   const isValidInitial = parsedInitial && !isNaN(parsedInitial.getTime());
   
   const [displayDay, setDisplayDay] = useState(isValidInitial ? String(parsedInitial.getDate()).padStart(2, "0") : "");
   const [displayMonth, setDisplayMonth] = useState(isValidInitial ? String(parsedInitial.getMonth() + 1).padStart(2, "0") : "");
   const [displayYear, setDisplayYear] = useState(isValidInitial ? String(parsedInitial.getFullYear()) : "");
   const [time, setTime] = useState(
      isValidInitial
         ? `${String(parsedInitial.getHours()).padStart(2, "0")}:${String(parsedInitial.getMinutes()).padStart(2, "0")}`
         : "00:00"
   );
   
   const [committedDay, setCommittedDay] = useState(isValidInitial ? parsedInitial.getDate() : null);
   const [committedMonth, setCommittedMonth] = useState(isValidInitial ? parsedInitial.getMonth() + 1 : null);
   const [committedYear, setCommittedYear] = useState(isValidInitial ? parsedInitial.getFullYear() : null);
   
   // Individual field error states to target the Bootstrap icon injection
   const [errors, setErrors] = useState({
      day: false,
      month: false,
      year: false,
   });
   const [errorMessage, setErrorMessage] = useState("");
   
   const isMounted = useRef(false);
   
   // Determine if the component has an active error state
   const hasError = errors.day || errors.month || errors.year || errorMessage;
   
   useEffect(() => {
      if (value) {
         const d = new Date(value);
         if (!isNaN(d.getTime())) {
            setDisplayDay(String(d.getDate()).padStart(2, "0"));
            setDisplayMonth(String(d.getMonth() + 1).padStart(2, "0"));
            setDisplayYear(String(d.getFullYear()));
            setCommittedDay(d.getDate());
            setCommittedMonth(d.getMonth() + 1);
            setCommittedYear(d.getFullYear());
            setTime(`${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`);
            setErrors({ day: false, month: false, year: false });
            setErrorMessage("");
         }
      } else if (value === "") {
         setDisplayDay("");
         setDisplayMonth("");
         setDisplayYear("");
         setCommittedDay(null);
         setCommittedMonth(null);
         setCommittedYear(null);
         setErrors({ day: false, month: false, year: false });
         setErrorMessage("");
      }
   }, [value]);
   
   useEffect(() => {
      if (!isMounted.current) {
         isMounted.current = true;
         return;
      }
      
      if (committedDay === null || committedMonth === null || committedYear === null || hasError) {
         return;
      }
      
      const [hours, minutes] = time.split(":").map(Number);
      const updatedDate = new Date(committedYear, committedMonth - 1, committedDay, hours || 0, minutes || 0);
      
      if (!isNaN(updatedDate.getTime())) {
         const formatted = formatDateTime(updatedDate, showTime, activeFormat);
         if (onChange) {
            onChange(formatted);
         }
      }
   }, [committedDay, committedMonth, committedYear, time, showTime, activeFormat, hasError]);
   
   const handleTextInput = (e, setter, maxLength) => {
      const val = e.target.value.replace(/\D/g, "");
      if (val.length <= maxLength) {
         setter(val);
      }
   };
   
   const handleCommitBlur = () => {
      // 1. Reset if all fields are empty
      if (!displayYear && !displayMonth && !displayDay) {
         setCommittedYear(null);
         setCommittedMonth(null);
         setCommittedDay(null);
         setErrors({ day: false, month: false, year: false });
         setErrorMessage("");
         return;
      }
      
      // 2. Do not fully evaluate until everything is filled out
      if (!displayYear || !displayMonth || !displayDay) {
         setCommittedYear(displayYear ? Number(displayYear) : null);
         setCommittedMonth(displayMonth ? Number(displayMonth) : null);
         setCommittedDay(displayDay ? Number(displayDay) : null);
         return;
      }
      
      const cleanYear = Number(displayYear);
      const cleanMonth = Number(displayMonth);
      const cleanDay = Number(displayDay);
      
      let activeErrors = { day: false, month: false, year: false };
      let globalMessage = "";
      
      // 3. Isolated contextual checks to flag exact textboxes
      if (cleanMonth < 1 || cleanMonth > 12) {
         activeErrors.month = true;
         globalMessage = "Please enter a valid month between 01 and 12.";
      }
      
      if (cleanYear < minYear || cleanYear > maxYear) {
         activeErrors.year = true;
         globalMessage = globalMessage
            ? "Please verify your month and year selections."
            : `Please enter a valid year between ${minYear} and ${maxYear}.`;
      }
      
      // Day verification depending on month/year boundaries
      if (!activeErrors.month) {
         const maxAllowedDays = getDaysInMonth(cleanMonth, cleanYear);
         if (cleanDay < 1 || cleanDay > maxAllowedDays) {
            activeErrors.day = true;
            if (!globalMessage) {
               if (cleanMonth === 2 && cleanDay === 29) {
                  globalMessage = `${cleanYear} is not a leap year. February only has 28 days.`;
               } else {
                  globalMessage = `The selected month only contains up to ${maxAllowedDays} days.`;
               }
            }
         }
      }
      
      // 4. State Assignment & Callback Hook Emitting
      setErrors(activeErrors);
      
      if (activeErrors.day || activeErrors.month || activeErrors.year) {
         setErrorMessage(globalMessage);
         setCommittedYear(null);
         setCommittedMonth(null);
         setCommittedDay(null);
         if (onValidationError) {
            onValidationError(globalMessage);
         }
      } else {
         setErrorMessage("");
         setDisplayYear(String(cleanYear));
         setDisplayMonth(String(cleanMonth).padStart(2, "0"));
         setDisplayDay(String(cleanDay).padStart(2, "0"));
         
         setCommittedYear(cleanYear);
         setCommittedMonth(cleanMonth);
         setCommittedDay(cleanDay);
      }
   };
   
   return (
      <div className="flex flex-col gap-1 w-full">
         <div className="flex gap-2 items-center">
            <div className="flex gap-2 w-full justify-content-between">
               
               {/* Day Input */}
               <div className="w-20">
                  <input
                     type="text"
                     inputMode="numeric"
                     value={displayDay}
                     placeholder="DD"
                     onChange={(e) => handleTextInput(e, setDisplayDay, 2)}
                     onBlur={handleCommitBlur}
                     className={`form-control text-center ${errors.day ? "is-invalid" : ""}`}
                  />
               </div>
               
               {/* Month Input */}
               <div className="w-20">
                  <input
                     type="text"
                     inputMode="numeric"
                     value={displayMonth}
                     placeholder="MM"
                     onChange={(e) => handleTextInput(e, setDisplayMonth, 2)}
                     onBlur={handleCommitBlur}
                     className={`form-control text-center ${errors.month ? "is-invalid" : ""}`}
                  />
               </div>
               
               {/* Year Input */}
               <div className="w-28">
                  <input
                     type="text"
                     inputMode="numeric"
                     value={displayYear}
                     placeholder="YYYY"
                     onChange={(e) => handleTextInput(e, setDisplayYear, 4)}
                     onBlur={handleCommitBlur}
                     className={`form-control text-center ${errors.year ? "is-invalid" : ""}`}
                  />
               </div>
            </div>
            
            {/* Time Input */}
            {showTime && (
               <div className="flex items-center gap-1">
                  <input
                     type="time"
                     value={time}
                     onChange={(e) => setTime(e.target.value)}
                     className="form-control"
                  />
               </div>
            )}
         </div>
         
         {/* Error Label Display */}
         {errorMessage && (
            <div className="text-danger text-xs mt-1 font-medium" style={{ color: "#dc3545" }}>
               {errorMessage}
            </div>
         )}
      </div>
   );
}